import React from 'react';

import { toast } from 'react-toastify';
import Select from 'react-select';
import DataTable from 'react-data-table-component';

import { AlertTriangle, Edit, Expand, Plus, Shrink, Trash } from 'lucide-react';
import { deleteOrganization, getOrganization } from './services/organizationServices';
import i18n from '../../../../i18n';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import NoData from '../../../common/NoData';
import OrganizationModal from './components/Organizationmodal';
import { ConfirmModal } from '../../../common/ConfirmModal';
import { getClients } from '../CashFlowDashboard/services/cashFlowService';
import FilterSection from '../../../common/Filter';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import Tooltip from '../../../common/Tooltip';

export class OrganizationsNew extends React.Component {
  constructor() {
    super();
    this.state = {
      organization: [],
      organizations: [],
      is_loading: false,
      search: {
        organization: '',
      },
      hideSidebar: false,
      limit: 15,
      page: 1,
      total: 0,

      // create / update modal
      showFormModal: false,
      selectedOrganization: null,

      // delete confirm
      showDeleteConfirm: false,
      deleteTarget: null,
      deleteLoading: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    // Parse URL query params
    const params = new URLSearchParams(window.location.search);
    const search = {
      organization: params.get('organization') || '',
    };
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;
    this.setState({ search, page, limit }, () => {
      this.fetchClientList();
      this.fetchOrganizations();
    });
  }
  // Update URL without refreshing page
  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null)),
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  fetchOrganizations = async () => {
    this.setState({ is_loading: true });
    const { limit, page } = this.state;
    const { organization } = this.state.search;
    try {
      const q = {
        page,
        limit,
      };

      if (organization) q.organization = organization;
      const response = await getOrganization(q);

      if (response.success !== false && response.data) {
        this.setState({ organization: response.data, total: response.meta.total });
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(i18n.t('something_went_wrong'));
    } finally {
      this.setState({ is_loading: false });
    }
  };

  async fetchClientList() {
    this.setState({ clientLoading: true });

    try {
      const response = await getClients();

      if (response.success) {
        this.setState({
          organizations: [
            {
              value: null,
              label: i18n.t('all') || 'All',
            },
            ...response.data.map((item) => ({
              value: item.id,
              label: item.organization_name,
            })),
          ],
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ clientLoading: false });
    }
  }

  /* ----------------------------------------------------------------------
   * Create / Update — both handled by OrganizationModal
   * -------------------------------------------------------------------- */

  handleCreate = () => {
    this.setState({ selectedOrganization: null, showFormModal: true });
  };

  handleEdit = (row) => {
    this.setState({ selectedOrganization: row, showFormModal: true });
  };

  handleFormModalClose = () => {
    this.setState({ showFormModal: false, selectedOrganization: null });
  };

  handleSaved = () => {
    this.setState({ showFormModal: false, selectedOrganization: null });
    this.fetchOrganizations();
  };

  /* ----------------------------------------------------------------------
   * Delete
   * -------------------------------------------------------------------- */

  handleDeleteClick = (row) => {
    this.setState({ deleteTarget: row, showDeleteConfirm: true });
  };

  handleDeleteConfirm = async () => {
    const { deleteTarget } = this.state;
    if (!deleteTarget) return;

    this.setState({ deleteLoading: true });

    try {
      const response = await deleteOrganization(deleteTarget.id);

      if (response && response.success !== false) {
        toast.success(response?.message || i18n.t('deleted_successfully'));
        this.setState({ showDeleteConfirm: false, deleteTarget: null });
        this.fetchOrganizations();
      } else {
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(i18n.t('something_went_wrong'));
    } finally {
      this.setState({ deleteLoading: false });
    }
  };

  handleClientChange = (selectedOption) => {
    const clientValue = selectedOption ? parseInt(selectedOption.value) : null;
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        organization: clientValue,
      },
    }));
  };

  handleSearch = () => {
    this.fetchClientList();
    this.fetchOrganizations();
  };

  handleClear = () => {
    const defaultSearch = {
      client: null,
    };

    this.setState({ search: defaultSearch, page: 1 }, () => {
      this.fetchClientList();
      this.fetchOrganizations();
    });
  };

  onChangePage = (page) => {
    this.setState({ page: page }, () => {
      this.fetchOrganizations();
    });
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit: limit, page: 1 }, () => {
      this.fetchOrganizations();
    });
  };

  getConfiguredAccount = (row, typeCode) => {
    const config = (row.payment_configurations || []).find((item) => item.type?.code === typeCode);
    return config?.organization_bank_account || null;
  };

  renderBankCell = (bank) => {
    if (!bank) return <span className="table-bank-empty">--</span>;

    return (
      <div className="table-bank-details table-bank-details-compact" title={bank.iban}>
        <div>
          <strong>{bank.bank_name}</strong>
        </div>
        <div>{bank.account_number}</div>
        <div>{bank.account_name}</div>
      </div>
    );
  };

  getColumns = () => {
    return [
      {
        name: i18n.t('id'),
        width: '90px',
        sortable: true,
        wrap: true,
        cell: (row) => row.id,
      },
      {
        name: i18n.t('image'),
        width: '90px',
        center: true,
        cell: (row) =>
          row.organization_image_url ? (
            <a href={row.organization_image_url} target="_blank" rel="noopener noreferrer">
              <img
                src={row.organization_image_url}
                alt={row.organization_name}
                style={{ width: 'auto', height: '30px' }}
              />
            </a>
          ) : (
            '--'
          ),
      },
      {
        name: i18n.t('title'),
        minWidth: '160px',
        wrap: true,
        cell: (row) => row.organization_name,
      },
      {
        name: i18n.t('title_arabic'),
        minWidth: '160px',
        wrap: true,
        cell: (row) => <span className="rtl-dir">{row.organization_name_ar}</span>,
      },
      {
        name: i18n.t('client'),
        width: '100px',
        center: true,
        cell: (row) => (
          <span className={`status-badge ${row.is_client ? 'status-badge-success' : 'status-badge-danger'}`}>
            {row.is_client ? i18n.t('yes') : i18n.t('no')}
          </span>
        ),
      },
      {
        name: i18n.t('description'),
        minWidth: '180px',
        wrap: true,
        cell: (row) => row.description,
      },
      {
        name: i18n.t('description_arabic'),
        minWidth: '180px',
        wrap: true,
        cell: (row) => <span className="rtl-dir">{row.description_ar}</span>,
      },
      {
        name: i18n.t('default_bank'),
        minWidth: '280px',
        wrap: true,
        cell: (row) => {
          const bank = row.default_mzadcom_bank_account;

          if (!bank) return '--';

          return (
            <div className="table-bank-details">
              <div>
                <strong>{bank.bank_name}</strong>
              </div>
              <div>{bank.account_name}</div>
              <div>{bank.account_number}</div>
              <div>{bank.iban}</div>
              <div>{bank.swift_code}</div>
              <div>{bank.currency}</div>
            </div>
          );
        },
      },
      {
        name: i18n.t('enrollment_account'),
        minWidth: '180px',
        wrap: true,
        cell: (row) => this.renderBankCell(this.getConfiguredAccount(row, 'auction_enrollment')),
      },
      {
        name: i18n.t('auction_payment_account'),
        minWidth: '180px',
        wrap: true,
        cell: (row) => this.renderBankCell(this.getConfiguredAccount(row, 'auction_payment')),
      },
      {
        name: i18n.t('actions'),
        width: '120px',
        center: true,
        cell: (row) => (
          <div className="table-action-icons">
            <span className="action-icon-wrap action-icon-edit">
              <Tooltip
                icon={Edit}
                tooltipText={i18n.t('edit')}
                onClick={() => this.handleEdit(row)}
                size={17}
                color="#0d6efd"
              />
            </span>

            <span className="action-icon-wrap action-icon-deactivate">
              <Tooltip
                icon={Trash}
                tooltipText={i18n.t('delete')}
                onClick={() => this.handleDeleteClick(row)}
                size={17}
                color="#dc3545"
              />
            </span>
          </div>
        ),
      },
    ];
  };

  render() {
    const {
      organization,
      is_loading,
      hideSidebar,
      showFormModal,
      selectedOrganization,
      showDeleteConfirm,
      deleteTarget,
      deleteLoading,
      organizations,
      clientLoading,
    } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner title={i18n.t('organizations')} subtitle={i18n.t('organizations_subtitle')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar ? <Shrink strokeWidth={1} /> : <Expand strokeWidth={1} />}
                      </span>
                    </div>
                    <div className="header-action">
                      <button className="button1 button1-gradient" onClick={this.handleCreate}>
                        <Plus size={18} />
                        <span>{i18n.t('create_organization')}</span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <FilterSection
                      loading={this.state.isloading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      <div className="react-select-wrapper">
                        <Select
                          options={organizations}
                          value={organizations.find((c) => c.value === this.state.search.organization) || null}
                          onChange={this.handleClientChange}
                          name="organization"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={clientLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={clientLoading}
                          isDisabled={clientLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('organization') || 'Organization'}</label>
                      </div>
                    </FilterSection>
                    <div className="Data-Listing">
                      {is_loading ? (
                        <TableSkeleton columns={7} rows={8} />
                      ) : organization.length ? (
                        <DataTable
                          responsive
                          columns={this.getColumns()}
                          data={organization}
                          customStyles={tableStyles}
                          highlightOnHover
                          pagination
                          paginationServer
                          paginationTotalRows={this.state.total}
                          paginationPerPage={this.state.limit}
                          paginationDefaultPage={this.state.page}
                          paginationRowsPerPageOptions={[15, 25, 50, 100]}
                          paginationComponentOptions={{
                            rowsPerPageText: i18n.t('rows_per_page'),
                            rangeSeparatorText: i18n.t('out'),
                          }}
                          onChangePage={this.onChangePage}
                          onChangeRowsPerPage={this.onChangeRowsPerPage}
                        />
                      ) : (
                        <NoData
                          message={i18n.t('no_organizations_available')}
                          actionText={i18n.t('refresh')}
                          onAction={this.fetchOrganizations}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Create / Update modal */}
        <OrganizationModal
          show={showFormModal}
          organization={selectedOrganization}
          onClose={this.handleFormModalClose}
          onSaved={this.handleSaved}
        />

        {/* Delete confirmation */}
        <ConfirmModal
          show={showDeleteConfirm}
          onClose={() => this.setState({ showDeleteConfirm: false, deleteTarget: null })}
          title={i18n.t('confirm_organization_deletion')}
          titleIcon={<AlertTriangle size={22} color="#dc3545" />}
          message={i18n.t('delete_organization_confirm_message', {
            name: deleteTarget?.organization_name,
          })}
          onConfirm={this.handleDeleteConfirm}
          onCancel={() => this.setState({ showDeleteConfirm: false, deleteTarget: null })}
          confirmText={i18n.t('delete')}
          cancelText={i18n.t('cancel')}
          loading={deleteLoading}
        />
      </React.Fragment>
    );
  }
}

export default OrganizationsNew;
